/**
 * Password Service
 * Epic: Sistema de Gerenciamento de Senhas NIST-Compliant
 *
 * Gerencia operações de senha:
 * - Trocar própria senha
 * - MASTER resetar senha de outro usuário
 * - Validar senha (feedback UI)
 */

import apiClient from './apiClient';

interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

interface ChangePasswordResponse {
  success: boolean;
  message: string;
}

interface ResetPasswordRequest {
  new_password: string;
}

interface ResetPasswordResponse {
  success: boolean;
  message: string;
  new_password: string;  // Para MASTER copiar
}

interface ValidatePasswordRequest {
  password: string;
  user_id?: number;
}

interface ValidatePasswordResponse {
  valid: boolean;
  errors: string[];
  strength_score: number;  // 0-100
  strength_label: string;  // "Fraca", "Média", "Boa", "Forte"
}

/**
 * Trocar própria senha
 * Endpoint: POST /api/password/change
 *
 * Validações:
 * - Senha atual deve estar correta
 * - Nova senha deve seguir NIST SP 800-63B (8-64 caracteres, não comum, etc.)
 */
export const changeOwnPassword = async (
  currentPassword: string,
  newPassword: string
): Promise<ChangePasswordResponse> => {
  try {
    const response = await apiClient.post<ChangePasswordResponse>('/password/change', {
      current_password: currentPassword,
      new_password: newPassword,
    });

    return response.data;
  } catch (error: any) {
    // Propagar erro com mensagem do backend
    const errorMessage = error.response?.data?.detail || 'Erro ao alterar senha';
    throw new Error(errorMessage);
  }
};

/**
 * MASTER resetar senha de outro usuário
 * Endpoint: POST /api/users/{user_id}/reset
 *
 * Apenas MASTER/ADMIN podem executar
 * Retorna a nova senha para MASTER copiar e comunicar ao usuário
 */
export const resetUserPassword = async (
  userId: number,
  newPassword: string
): Promise<ResetPasswordResponse> => {
  try {
    const response = await apiClient.post<ResetPasswordResponse>(
      `/password/users/${userId}/reset`,
      {
        new_password: newPassword,
      }
    );

    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || 'Erro ao resetar senha';
    throw new Error(errorMessage);
  }
};

/**
 * Validar senha (opcional - para feedback UI em tempo real)
 * Endpoint: POST /api/password/validate
 *
 * Retorna:
 * - valid: true/false
 * - errors: lista de erros de validação
 * - strength_score: 0-100
 * - strength_label: "Fraca", "Média", "Boa", "Forte"
 *
 * Útil para mostrar feedback enquanto usuário digita nova senha
 */
export const validatePassword = async (
  password: string,
  userId?: number
): Promise<ValidatePasswordResponse> => {
  try {
    const response = await apiClient.post<ValidatePasswordResponse>('/password/validate', {
      password,
      user_id: userId,
    });

    return response.data;
  } catch (error: any) {
    // Em caso de erro, retornar validação falha
    return {
      valid: false,
      errors: ['Erro ao validar senha'],
      strength_score: 0,
      strength_label: 'Fraca',
    };
  }
};

/**
 * Helper: Obter cor baseada no score de força
 *
 * @param score - Score 0-100
 * @returns Cor CSS (vermelho/laranja/amarelo/verde)
 */
export const getStrengthColor = (score: number): string => {
  if (score < 30) return '#dc3545';  // Vermelho (Fraca)
  if (score < 60) return '#fd7e14';  // Laranja (Média)
  if (score < 80) return '#ffc107';  // Amarelo (Boa)
  return '#28a745';  // Verde (Forte)
};

/**
 * Helper: Validar tamanho mínimo no frontend (antes de chamar backend)
 *
 * @param password - Senha a validar
 * @returns true se >= 8 caracteres
 */
export const hasMinimumLength = (password: string): boolean => {
  return password.length >= 8 && password.length <= 64;
};
